import React, { useEffect, useRef } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import ButtonGroup, { ButtonOption } from '@amzn/meridian/button-group';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import SearchField from '@amzn/meridian/search-field';
import AppliedFilterTags from 'src/components/shared/appliedFilterTags/appliedFilterTags';
import LoadingSpinner from 'src/components/shared/loadingSpinner/loadingSpinner';
import NewFeatureBanner from 'src/components/shared/newFeatureBanner/newFeatureBanner';
import PaginationWithDropdown from 'src/components/shared/paginationWithDropdown/paginationWithDropdown';
import ToggleFilterButton from 'src/components/shared/toggleFilterButton/toggleFilterButton';
import { COUNTRY_ID_MAPPING } from 'src/config/CountryIdMapping';
import { JOB_MODIFICATIONS_WIKI_LINK, T2_JOB_REFERENCE_CARD } from 'src/config/ExternalLinks';
import {
  getOpenAndClosedPlacementsBySiteId,
  removeTagAndUpdateFilters,
  selectHasTransferredInPlacements,
  setCurrentPage,
  setGlobalFilterOptions,
  setIsAdvancedFilterSheetOpen,
  setPerPage,
  setSearchField,
  setSelectedFilterOptions,
} from 'src/store/placementOverviewPageSlice';
import { setIsSiteSelectionDisabled, setSelectedSite } from 'src/store/siteSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { ApiCallStatus } from 'src/store/type';
import { setPreferredSite } from 'src/store/userSlice';
import { type AccommodationTypeName } from 'src/types/accommodationTypes';

import {
  DEFAULT_PLACEMENT_OVERVIEW_TAB,
  isPlacementOverviewTab,
  PLACEMENT_OVERVIEW_TABS,
  type PlacementOverviewTab,
} from './placementOverviewTabs';
import './placementsOverview.scss';

import AdvancedFilterSheet from './advancedFilterSheet/advancedFilterSheet';
import PlacementOverviewTable from './placementOverviewTable/placementOverviewTable';
import TabGroup from './tabGroup/tabGroup';

const ALL_PLACEMENT_TYPES = '__allPlacementTypes__';
type LabeledPlacementType = Exclude<AccommodationTypeName, 'ANY'>;
const PLACEMENT_TYPE_LABELS: Readonly<Record<LabeledPlacementType, string>> = {
  AUSTIN: 'Work Related (AUSTIN)',
  DALI: 'Non-Work Related (DALI)',
  BALI: 'Non-Work Related (BALI)',
};
const isLabeledPlacementType = (placementType: string): placementType is LabeledPlacementType =>
  Object.prototype.hasOwnProperty.call(PLACEMENT_TYPE_LABELS, placementType);

const resolvePlacementOverviewTab = (tab: string | undefined): PlacementOverviewTab | undefined =>
  tab && isPlacementOverviewTab(tab) ? tab : undefined;

const PlacementsOverview = () => {
  const dispatch = useAppDispatch();
  const { tab, site } = useParams();
  const {
    placementsStatus,
    searchField,
    appliedFilterOptions,
    availableFilterOptions,
    globalFilterOptions,
    currentPage,
    totalPages,
    perPage,
    filterTags,
  } = useAppSelector((state) => state.placementOverviewPage);
  const navigate = useNavigate();
  const { userLoading, featureFlags, isFeatureFlagsLoaded } = useAppSelector((state) => state.user);
  const { selectedSite, allSites, allowedSites } = useAppSelector((state) => state.sites);
  const hasTransferredInPlacements = useAppSelector(selectHasTransferredInPlacements);
  const routeTab = resolvePlacementOverviewTab(tab);
  const currentTab = routeTab ?? DEFAULT_PLACEMENT_OVERVIEW_TAB;
  const siteRef = useRef<string | undefined>(site);
  const hasSelectedSite = selectedSite.site !== '';
  const placementsFailedToLoad = placementsStatus === ApiCallStatus.Error;
  const placementsLoaded = placementsStatus === ApiCallStatus.Success;
  const placementsAreLoading = hasSelectedSite && !placementsFailedToLoad && !placementsLoaded;
  const shouldShowPlacementsPanel = hasSelectedSite || placementsFailedToLoad;
  const placementLoadErrorTitle = selectedSite.site
    ? `Placements could not be loaded for ${selectedSite.site}.`
    : 'Placements could not be loaded.';
  const canRetryPlacements = selectedSite.id > 0;
  const availablePlacementTypes = availableFilterOptions.availablePlacementTypes;
  const selectedPlacementType = globalFilterOptions.placementType ?? ALL_PLACEMENT_TYPES;
  const getPlacementTypeLabel = (placementType: string) =>
    isLabeledPlacementType(placementType) ? PLACEMENT_TYPE_LABELS[placementType] : placementType;
  const selectPlacementType = (placementType: string) => {
    dispatch(
      setGlobalFilterOptions({
        placementType: placementType === ALL_PLACEMENT_TYPES ? null : placementType,
      }),
    );
  };
  const selectTab = (newTab: PlacementOverviewTab) => {
    const targetSite = selectedSite.site || site;
    if (targetSite) {
      navigate(`/placementsOverview/${targetSite}/${newTab}`);
      dispatch(setCurrentPage(1));
    }
  };

  // Keep the URL and placement data synchronized with selected-site changes
  useEffect(() => {
    if (userLoading === 'fulfilled' && selectedSite.site) {
      if (selectedSite.site && selectedSite.site !== siteRef.current) {
        siteRef.current = selectedSite.site;
        navigate(`/placementsOverview/${siteRef.current}/${DEFAULT_PLACEMENT_OVERVIEW_TAB}`);
      }
      if (!allowedSites.map((allowedSite) => allowedSite.site).includes(siteRef.current!)) {
        navigate('unauthorized');
      }
      dispatch(getOpenAndClosedPlacementsBySiteId(selectedSite.id));
    }
    return () => {
      dispatch(setIsSiteSelectionDisabled(false));
    };
  }, [userLoading, selectedSite]);

  // Resolve route parameters into selected-site state and canonical tab navigation
  useEffect(() => {
    if (siteRef && allSites.length > 0) {
      const siteObj = allSites.filter((s) => s.site === siteRef.current)[0];
      if (siteObj) {
        dispatch(setSelectedSite(siteObj));
        dispatch(setPreferredSite(siteObj.id));
        if (tab && !routeTab) {
          navigate(`/placementsOverview/${siteObj.site}/${DEFAULT_PLACEMENT_OVERVIEW_TAB}`);
          dispatch(setCurrentPage(1));
        }
      }
    }
  }, [tab, routeTab, siteRef, allSites, dispatch, navigate]);

  // Redirect unavailable Transferred in routes only after feature or route-site data resolves
  useEffect(() => {
    const isTransferredInRoute = routeTab === PLACEMENT_OVERVIEW_TABS.TRANSFERRED_IN;
    const isTransferFeatureUnavailable = isFeatureFlagsLoaded && !featureFlags.PLACEMENT_TRANSFER;
    const hasLoadedRouteSitePlacements = placementsLoaded && selectedSite.site === site;
    const hasNoTransferredInPlacements = hasLoadedRouteSitePlacements && !hasTransferredInPlacements;

    if (site && isTransferredInRoute && (isTransferFeatureUnavailable || hasNoTransferredInPlacements)) {
      navigate(`/placementsOverview/${site}/${DEFAULT_PLACEMENT_OVERVIEW_TAB}`, { replace: true });
      dispatch(setCurrentPage(1));
    }
  }, [
    site,
    routeTab,
    selectedSite.site,
    placementsLoaded,
    hasTransferredInPlacements,
    isFeatureFlagsLoaded,
    featureFlags.PLACEMENT_TRANSFER,
    dispatch,
    navigate,
  ]);

  return (
    <Box backgroundColor="principal" minHeight="100%" spacingInset="450 500">
      <Column spacingInset="none none 400 none">
        {/* Alerts */}
        {featureFlags.JOB_MODIFICATIONS && selectedSite.jobModificationsEnabled && (
          <NewFeatureBanner
            label="jobModificationsFeatureAlert"
            text={
              <>
                Pre-approved job modifications are now available in the RTW tool to support efficient placement
                decisions.
              </>
            }
            link={JOB_MODIFICATIONS_WIKI_LINK}
          />
        )}
        {featureFlags.T2_PLUS_EXPERIENCE && (
          <NewFeatureBanner
            label="t2ExperienceFeatureAlert"
            text={
              <>
                The RTW tool now supports accommodation placement entry for associates at all job levels. Questions on
                Return to Work's T2+ experience? Please refer to our
              </>
            }
            link={T2_JOB_REFERENCE_CARD}
            linkText="Quick Reference Card."
          />
        )}
        {featureFlags.TASK_GROUPING && selectedSite.countryId === COUNTRY_ID_MAPPING.CANADA_COUNTRY_ID && (
          <NewFeatureBanner
            label="taskGroupingCanadaFeatureAlert"
            text="Streamlined restriction entry is now available for Canadian sites."
          />
        )}
        <NewFeatureBanner
          label="twpaPdfUpdate"
          text="Updates have been made to the temporary work placement acknowledgement letter."
        />

        {/* Main content */}
        <Heading level={1} type="h500">{`Placements: ${selectedSite.site}`}</Heading>
        <Row widths={['fill', 'fit']} alignmentVertical="center">
          <Box>
            {hasSelectedSite && placementsLoaded && availablePlacementTypes.length > 0 && (
              <ButtonGroup value={selectedPlacementType} onChange={selectPlacementType} legend="Source">
                <ButtonOption value={ALL_PLACEMENT_TYPES}>All Case Sources</ButtonOption>
                {availablePlacementTypes.map((placementType: string) => (
                  <ButtonOption key={placementType} value={placementType}>
                    {getPlacementTypeLabel(placementType)}
                  </ButtonOption>
                ))}
              </ButtonGroup>
            )}
          </Box>
          <Button type="primary" onClick={() => navigate('/newJMR')}>
            Create Placement
          </Button>
        </Row>
        {shouldShowPlacementsPanel && (
          <Box type="outline" spacingInset="400">
            {placementsAreLoading ? (
              <LoadingSpinner label="Loading placements" />
            ) : placementsFailedToLoad ? (
              <Row>
                <Alert type="error" title={placementLoadErrorTitle}>
                  {' '}
                </Alert>
                {canRetryPlacements && (
                  <Button onClick={() => dispatch(getOpenAndClosedPlacementsBySiteId(selectedSite.id))}>
                    Try again
                  </Button>
                )}
              </Row>
            ) : (
              <Column spacing="300">
                <Row widths={['grid-3', 'fit']} alignmentVertical="center">
                  <SearchField
                    value={searchField}
                    onChange={(newSearchField) => dispatch(setSearchField(newSearchField))}
                    placeholder="Search..."
                    onSubmit={() => {}}
                  />
                  <ToggleFilterButton
                    aria-label="Open Placements Overview Filter"
                    data-testid="placements-overview__filter-icon"
                    tooltipTitle="Open Placements Overview Filter"
                    onClick={() => {
                      dispatch(setIsAdvancedFilterSheetOpen(true));
                      dispatch(setSelectedFilterOptions(appliedFilterOptions));
                    }}
                  />
                </Row>
                <TabGroup currentTab={currentTab} onTabChange={selectTab} />
                <AppliedFilterTags
                  filterTags={filterTags}
                  onDismissTag={(tag) => dispatch(removeTagAndUpdateFilters(tag))}
                />
                <PlacementOverviewTable currentTab={currentTab} />
                <PaginationWithDropdown
                  currentPage={currentPage}
                  totalPages={totalPages}
                  perPage={perPage}
                  setPerPage={(newPerPage) => dispatch(setPerPage(newPerPage))}
                  setCurrentPage={(newPage) => dispatch(setCurrentPage(newPage))}
                />
              </Column>
            )}
          </Box>
        )}
        <AdvancedFilterSheet />
      </Column>
    </Box>
  );
};

export default PlacementsOverview;
